// Shell state shared by the chrome and the views: layout prefs (persisted),
// the docked inspector slot, the context-list slot a destination fills, the
// per-view key handlers and their printed caps, the toast, and the help
// sheet. Views read it through useShell(); the chrome owns the writes. State
// is replaced, never mutated.
//
// Two slots exist because the contract gives a destination two places inside
// the persistent chrome: the inspector column (340) and the context list
// under its group rows (shell.html: "destination context here"). Both empty
// on a destination change so nothing leaks between views.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { HonestStateVariant } from '../states/HonestState';
import { useToast, type ToastState } from '../ui/Toast';
import { useView } from '../router/useView';
import type { KeyHandlers, KeyHint } from './useKeys';
import {
  DEFAULT_PREFS,
  SHELL_KEYS,
  browserStorage,
  clampListWidth,
  readPrefs,
  tapeClassOf,
  toggled,
  writeKey,
  type OpenState,
  type ShellPrefs,
} from './shellPrefs';

export interface InspectorContent {
  readonly title: ReactNode;
  readonly subtitle?: ReactNode;
  readonly artwork?: ReactNode;
  readonly body: ReactNode;
}

// What a destination adds to the persistent chrome's context list: its own
// rows (sources, saved views, boards, a research dock) and, when it needs
// one, a different column width (Coin Checker docks at 320).
export interface ListContext {
  readonly body: ReactNode;
  readonly width?: number;
}

// A destination's keyboard contribution: the rows the "?" sheet prints and
// the caps the status bar shows. Only keys the view actually handles.
export interface ViewKeys {
  readonly hints: ReadonlyArray<KeyHint>;
  readonly caps: ReadonlyArray<string>;
}

export interface DestinationStatus {
  readonly records: string;
  readonly observedAt: string | null;
  readonly freshnessLabel: string;
  readonly emptyFreshness: string;
  readonly state: {
    readonly variant: HonestStateVariant;
    readonly reason: string;
  };
  readonly retained: boolean;
}

export const EMPTY_VIEW_KEYS: ViewKeys = { hints: [], caps: [] };

export interface ShellApi {
  readonly prefs: ShellPrefs;
  readonly tapeOpen: boolean;
  readonly listOpen: boolean;
  readonly inspectorOpen: boolean;
  readonly toggleList: () => void;
  readonly toggleTape: () => void;
  readonly toggleInspector: () => void;
  readonly openInspector: () => void;
  readonly closeInspector: () => void;
  readonly setListWidth: (width: number, persist: boolean) => void;
  readonly toggleChecklist: () => void;
  readonly inspector: InspectorContent | null;
  readonly setInspector: (content: InspectorContent | null) => void;
  readonly listContext: ListContext | null;
  readonly setListContext: (content: ListContext | null) => void;
  readonly keyHandlers: KeyHandlers;
  readonly setKeyHandlers: (handlers: KeyHandlers) => void;
  readonly viewKeys: ViewKeys;
  readonly setViewKeys: (keys: ViewKeys) => void;
  readonly destinationStatus: DestinationStatus | null;
  readonly setDestinationStatus: (status: DestinationStatus | null) => void;
  readonly toast: ToastState;
  readonly helpOpen: boolean;
  readonly setHelpOpen: (open: boolean) => void;
  readonly menuOpen: boolean;
  readonly setMenuOpen: (open: boolean) => void;
}

const ShellCtx = createContext<ShellApi | null>(null);

export function ShellProvider({ children }: { readonly children: ReactNode }) {
  const storage = useMemo(() => browserStorage(), []);
  const [prefs, setPrefs] = useState<ShellPrefs>(() => readPrefs(storage));
  const [inspector, setInspector] = useState<InspectorContent | null>(null);
  const [listContext, setListContext] = useState<ListContext | null>(null);
  const [keyHandlers, setKeyHandlers] = useState<KeyHandlers>({});
  const [viewKeys, setViewKeys] = useState<ViewKeys>(EMPTY_VIEW_KEYS);
  const [destinationStatus, setDestinationStatus] = useState<DestinationStatus | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const toast = useToast();
  const { view } = useView();
  const tapeClass = tapeClassOf(view);

  const setOpen = useCallback(
    (key: 'list' | 'inspector' | 'checklist', next: OpenState) => {
      setPrefs((current) => ({ ...current, [key]: next }));
      writeKey(storage, SHELL_KEYS[key], next);
    },
    [storage],
  );

  const toggleList = useCallback(() => setOpen('list', toggled(prefs.list)), [prefs.list, setOpen]);
  const toggleInspector = useCallback(
    () => setOpen('inspector', toggled(prefs.inspector)),
    [prefs.inspector, setOpen],
  );
  const openInspector = useCallback(() => setOpen('inspector', 'open'), [setOpen]);
  const closeInspector = useCallback(() => setOpen('inspector', 'closed'), [setOpen]);
  const toggleChecklist = useCallback(
    () => setOpen('checklist', toggled(prefs.checklist)),
    [prefs.checklist, setOpen],
  );

  const toggleTape = useCallback(() => {
    const next = toggled(prefs.tape[tapeClass]);
    setPrefs((current) => ({ ...current, tape: { ...current.tape, [tapeClass]: next } }));
    writeKey(storage, `${SHELL_KEYS.tape}.${tapeClass}`, next);
  }, [prefs.tape, storage, tapeClass]);

  const setListWidth = useCallback(
    (width: number, persist: boolean) => {
      const clamped = clampListWidth(width);
      setPrefs((current) =>
        current.listWidth === clamped ? current : { ...current, listWidth: clamped },
      );
      if (persist) writeKey(storage, SHELL_KEYS.listWidth, String(clamped));
    },
    [storage],
  );

  // Both slots and the key registration empty when the destination changes
  // (state reset during render, keyed on the view).
  const [lastView, setLastView] = useState(view);
  if (lastView !== view) {
    setLastView(view);
    setInspector(null);
    setListContext(null);
    setKeyHandlers({});
    setViewKeys(EMPTY_VIEW_KEYS);
    setDestinationStatus(null);
  }

  const value = useMemo<ShellApi>(
    () => ({
      prefs,
      tapeOpen: prefs.tape[tapeClass] === 'open',
      listOpen: prefs.list === 'open',
      inspectorOpen: prefs.inspector === 'open',
      toggleList,
      toggleTape,
      toggleInspector,
      openInspector,
      closeInspector,
      setListWidth,
      toggleChecklist,
      inspector,
      setInspector,
      listContext,
      setListContext,
      keyHandlers,
      setKeyHandlers,
      viewKeys,
      setViewKeys,
      destinationStatus,
      setDestinationStatus,
      toast,
      helpOpen,
      setHelpOpen,
      menuOpen,
      setMenuOpen,
    }),
    [
      prefs,
      tapeClass,
      toggleList,
      toggleTape,
      toggleInspector,
      openInspector,
      closeInspector,
      setListWidth,
      toggleChecklist,
      inspector,
      listContext,
      keyHandlers,
      viewKeys,
      destinationStatus,
      toast,
      helpOpen,
      menuOpen,
    ],
  );

  return <ShellCtx.Provider value={value}>{children}</ShellCtx.Provider>;
}

export function useShell(): ShellApi {
  const api = useContext(ShellCtx);
  if (!api) throw new Error('useShell must be used inside ShellProvider');
  return api;
}

// Views register their row handlers (j, k, Enter, c, w, a, b, digits) for the
// shell's single listener, plus the hints and caps the chrome prints for them.
export function useShellKeys(handlers: KeyHandlers, keys: ViewKeys = EMPTY_VIEW_KEYS): void {
  const { setKeyHandlers, setViewKeys } = useShell();
  useEffect(() => {
    setKeyHandlers(handlers);
    setViewKeys(keys);
    return () => {
      setKeyHandlers({});
      setViewKeys(EMPTY_VIEW_KEYS);
    };
    // Handlers are recreated per render by callers; register once per mount and
    // when the caller passes a memoised object.
  }, [handlers, keys, setKeyHandlers, setViewKeys]);
}

// Views fill the context list under their group rows. Cleared on unmount and
// on a destination change.
export function useListContext(content: ListContext | null): void {
  const { setListContext } = useShell();
  const last = useRef<ListContext | null>(null);
  const seeded = useRef(false);
  // Callers naturally build this object inline. Depending on its identity would
  // publish on every render, re-render the shell, produce a new object, and loop
  // forever, so compare by value and publish only on a real change.
  useEffect(() => {
    const prev = last.current;
    const unchanged =
      seeded.current &&
      (prev === content ||
        (prev !== null &&
          content !== null &&
          prev.body === content.body &&
          prev.width === content.width));
    if (unchanged) return;
    seeded.current = true;
    last.current = content;
    setListContext(content);
  });
  useEffect(
    () => () => {
      // Forgetting what was published is part of clearing it. StrictMode mounts,
      // unmounts and remounts every view in development while keeping these refs,
      // so a caller whose memo survives the remount compared equal to the value
      // this cleanup had just wiped and never republished: the destination's own
      // rows vanished from the list for the rest of the session. Views were
      // working around it with a state bump after mount.
      seeded.current = false;
      last.current = null;
      setListContext(null);
    },
    [setListContext],
  );
}

function sameDestinationStatus(
  left: DestinationStatus | null,
  right: DestinationStatus | null,
): boolean {
  return (
    left === right ||
    (left !== null &&
      right !== null &&
      left.records === right.records &&
      left.observedAt === right.observedAt &&
      left.freshnessLabel === right.freshnessLabel &&
      left.emptyFreshness === right.emptyFreshness &&
      left.state.variant === right.state.variant &&
      left.state.reason === right.state.reason &&
      left.retained === right.retained)
  );
}

// A destination can replace the archive-wide footer facts with the facts it
// already loaded for its own surface. The slot clears on unmount and view
// change, following the same StrictMode-safe publication contract as the list.
export function useDestinationStatus(status: DestinationStatus | null): void {
  const { setDestinationStatus } = useShell();
  const last = useRef<DestinationStatus | null>(null);
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current && sameDestinationStatus(last.current, status)) return;
    seeded.current = true;
    last.current = status;
    setDestinationStatus(status);
  });
  useEffect(
    () => () => {
      seeded.current = false;
      last.current = null;
      setDestinationStatus(null);
    },
    [setDestinationStatus],
  );
}

export { DEFAULT_PREFS };
